import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, Check, Pencil, Printer } from 'lucide-react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { DriedTeaPurchaseFormDialog } from '@/components/dried-tea-purchases/dried-tea-purchase-form-dialog'
import { DriedTeaPurchasePrintDialog } from '@/components/dried-tea-purchases/dried-tea-purchase-print-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    useDriedTeaPurchase,
    useMarkDriedBallotReturned,
} from '@/hooks/use-dried-tea-purchase-query'
import { formatQty, formatVN } from '@/lib/format'
import { gradeLabel } from '@/lib/tea-purchase-labels'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/dried-tea-purchases/$id',
)({
    component: DriedTeaPurchaseDetailPage,
})

function DriedTeaPurchaseDetailPage() {
    const { id } = Route.useParams()
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')

    const { data, isLoading } = useDriedTeaPurchase(id)
    const markBallot = useMarkDriedBallotReturned()
    const ballotWorkflow = useWorkflowTracking(
        'dried_tea_purchase_ballot_return',
        { autoStart: false, attemptKey: id, operation: 'update' },
    )
    const [editOpen, setEditOpen] = useState(false)
    const [printOpen, setPrintOpen] = useState(false)

    if (isLoading || !data) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    const d = data
    const sources = d.sources ?? []

    // Cùng cách đo với ô tick ở danh sách: huỷ xác nhận = closed, chỉ tính
    // xong khi API trả về đúng phiếu.
    const handleBallot = () => {
        ballotWorkflow.step('warning_shown')
        if (!window.confirm(m.tea_purchase_ballot_confirm())) {
            ballotWorkflow.finish('closed')
            return
        }
        ballotWorkflow.step('warning_confirmed')
        void ballotWorkflow
            .run(
                () => markBallot.mutateAsync(d.id),
                (res) => res.data?.id,
            )
            .catch(() => {})
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center justify-end gap-2">
                <div className="flex items-center gap-2">
                    {d.ballot_returned ? (
                        <Badge variant="secondary">
                            <Check className="size-3 mr-1" />
                            {m.tea_purchase_ballot_returned()}
                        </Badge>
                    ) : (
                        canWrite && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBallot}
                                disabled={markBallot.isPending}
                            >
                                {m.tea_purchase_ballot_action()}
                            </Button>
                        )
                    )}
                    {canWrite && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditOpen(true)}
                        >
                            <Pencil className="size-4 mr-1" />
                            {m.common_edit()}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPrintOpen(true)}
                    >
                        <Printer className="size-4 mr-1" />
                        {m.tea_purchase_print_finished()}
                    </Button>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <PageBackButton asChild>
                    <Link to="/dried-tea-purchases">
                        <ArrowLeft className="size-4" />
                    </Link>
                </PageBackButton>
                <h1 className="text-base md:text-2xl font-semibold font-mono">
                    {d.code}
                </h1>
                {sources.length > 0 ? (
                    <Badge variant="outline">
                        {m.dtp_sources_count({
                            count: String(sources.length),
                        })}
                    </Badge>
                ) : (
                    <Badge variant="secondary">{m.dtp_sources_none()}</Badge>
                )}
                {d.has_active_in_note && (
                    <span className="text-xs text-muted-foreground">
                        {m.dtp_locked_hint()}
                    </span>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{m.dtp_detail_title()}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <Row
                            label={m.tea_purchase_grower()}
                            value={d.grower_name || '—'}
                        />
                        <Row
                            label={m.dtp_quantity()}
                            value={`${formatQty(d.quantity)} kg`}
                        />
                        <Row
                            label={m.dtp_grade()}
                            value={
                                d.tea_grade ? gradeLabel[d.tea_grade]() : '—'
                            }
                        />
                        <Row
                            label={m.tea_purchase_purchase_date()}
                            value={formatVN(d.purchase_date)}
                        />
                        <Row
                            label={m.dtp_remaining()}
                            value={`${formatQty(d.remaining_dry_kg ?? 0)} kg`}
                        />
                        <Row
                            label={m.tea_purchase_note()}
                            value={d.note || '—'}
                        />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{m.dtp_sources()}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {sources.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                                {m.dtp_sources_none()}
                            </p>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{m.dtp_code()}</TableHead>
                                        <TableHead>
                                            {m.tea_purchase_purchase_date()}
                                        </TableHead>
                                        <TableHead className="text-right">
                                            {m.dtp_fresh_kg()}
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sources.map((s) => (
                                        <TableRow key={s.tea_purchase_id}>
                                            <TableCell className="font-mono text-sm">
                                                <Link
                                                    to="/tea-purchases/$id"
                                                    params={{
                                                        id: s.tea_purchase_id,
                                                    }}
                                                    className="text-primary hover:underline"
                                                >
                                                    {s.code}
                                                </Link>
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {formatVN(s.purchase_date)}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {formatQty(s.quantity)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell className="font-medium">
                                            {m.tea_purchase_slip_total()}
                                        </TableCell>
                                        <TableCell />
                                        <TableCell className="text-right tabular-nums font-medium">
                                            {formatQty(
                                                sources.reduce(
                                                    (sum, s) =>
                                                        sum + s.quantity,
                                                    0,
                                                ),
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            <DriedTeaPurchaseFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                dried={d}
            />
            <DriedTeaPurchasePrintDialog
                open={printOpen}
                onOpenChange={(v) => !v && setPrintOpen(false)}
                dried={d}
                entry="dried_detail"
            />
        </div>
    )
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex">
            <span className="w-1/3 text-muted-foreground">{label}</span>
            <span className="flex-1">{value}</span>
        </div>
    )
}
